import { Event } from "../../../base/common/events.js";
import { createDecorator } from "../../instantiation/common/registry.js";

export const IResourceService = createDecorator<IResourceService>('resourceService');

export type ResourceKind = "material" | "texture" | "geometry" | "mixer" | "model" | "clip" | (string & {});

/**
 * A stable consumer-key for the usage index. Most resources have a three uuid,
 * but loaded-model wrappers (glTF `{ scene, animations }`) often have none — so
 * fall back to the entry's stable `kind#id`. Used on BOTH sides: the tracker
 * records model→resource usages under this key, the resource graph looks them
 * up under it. Must be applied consistently or model edges won't resolve.
 */
export function resourceUsageUuid(entry: { uuid?: string; kind: string; id: number }): string {
    return entry.uuid && entry.uuid.length ? entry.uuid : `${entry.kind}#${entry.id}`;
}

/**
 * A weakly-held registry entry for a scene resource (material, texture, geometry, …).
 *
 * The entry itself only holds primitives (id, uuid, name/type snapshots) plus a WeakRef —
 * it never extends the lifetime of the underlying object. This matters because the editor
 * also runs as a chrome-extension inspector injected into third-party pages, where we must
 * not retain host-page objects. The only strong retention is an explicit user pin
 * (see {@link IResourceService.pin}).
 */
export interface IResourceEntry {
    /** Stable handle; safe map/render key that survives GC of the resource. */
    readonly id: number;
    readonly kind: ResourceKind;
    /** three.js uuid snapshot (primitive — safe to hold after GC). */
    readonly uuid: string | undefined;
    /** Name snapshot; refreshed on re-registration, remains readable after GC. */
    readonly name: string;
    /** e.g. "MeshStandardMaterial" */
    readonly type: string;
    readonly pinned: boolean;
    /** False once the resource has been garbage collected. */
    readonly alive: boolean;
    /**
     * Source-file size in bytes when known (loaded model files: the raw buffer
     * the loader parsed). Primitive snapshot — safe to hold after GC.
     */
    readonly byteSize?: number;
    /** The live resource, or undefined after GC. Never store the result long-term. */
    deref(): object | undefined;
}

export interface ResourcesChangedEvent {
    readonly added: readonly IResourceEntry[];
    /** Entries whose resource was garbage collected. */
    readonly collected: readonly IResourceEntry[];
    readonly pinChanged: readonly IResourceEntry[];
    /** Entries whose name/type snapshot or usage set was refreshed. */
    readonly updated: readonly IResourceEntry[];
}

/**
 * Primitive snapshot of a resource consumer — the mesh using a geometry/material,
 * the material sampling a texture, the scene whose environment/background it is.
 * Snapshots only (uuid + name/type strings), NEVER a live reference: usage data
 * must not extend host-object lifetimes. May be stale between sweep passes.
 */
export interface ResourceUsage {
    readonly uuid: string;
    readonly name: string;
    readonly type: string;
    /**
     * The binding names by which this consumer references the resource — a
     * material's texture SLOTS ("map", "normalMap") or uniform names. Accumulated
     * per consumer within a usage pass (a texture bound to two slots on one
     * material lists both). Absent for bindings with no meaningful slot (a mesh
     * → geometry, a scene → environment).
     */
    readonly slots?: readonly string[];
    /**
     * A WEAK handle to the live consuming object (a mesh, a scene) — lets tooling
     * resolve it back without retaining it: e.g. clicking a mesh node in the
     * resource graph selects its hierarchy row. Absent for consumers recorded
     * from a plain snapshot (the reverse index built from uuids alone).
     */
    readonly ref?: WeakRef<object>;
}

/**
 * Weak registry of scene resources for debugging/browsing tooling.
 *
 * Registration is always weak: entries observe their resource via WeakRef +
 * FinalizationRegistry and disappear (fire `collected`) when the host/engine
 * drops the object. Pinning is the single opt-in strong retention.
 */
export interface IResourceService {
    readonly _serviceBrand: undefined;

    /** Batched + debounced (~100ms) — safe to rebuild UI from directly. */
    readonly onDidChangeResources: Event<ResourcesChangedEvent>;

    /**
     * Weakly registers a resource. Registering an already-known live resource is a
     * cheap no-op (it may refresh the name/type snapshot and fire `updated`).
     *
     * `options.label` is a display-name fallback used while the resource itself has
     * no/an empty `name` — e.g. an AnimationMixer (label = its root's name) or a
     * loaded model root (label = the source file name). A real `name` on the
     * resource always wins. `options.type` similarly overrides the constructor-name
     * fallback (useful when the constructor name is minified or a plain "Object"),
     * but a real string `type` property on the resource always wins.
     */
    register(resource: object, kind: ResourceKind, options?: { label?: string, type?: string, byteSize?: number }): void;

    /** Source identity captured by the loader; roots remain weakly held. */
    recordModelSource(model: object, root: object, uri: string): void;
    modelSources(): Iterable<{ model: IResourceEntry; root: object; uri: string }>;

    /** Whether the resource is currently registered (and not yet collected). */
    has(resource: object): boolean;

    /** The registry entry for a live resource, or undefined if not registered
     *  (or already collected). O(1) — the reverse object→id map. */
    getEntry(resource: object): IResourceEntry | undefined;

    /**
     * All entries, optionally filtered by kind. Entries whose resource died but whose
     * finalizer has not run yet are pruned lazily during iteration.
     */
    entries(kind?: ResourceKind): Iterable<IResourceEntry>;

    /** Liveness-filtered convenience iterator over entry + dereferenced resource. */
    resources(kind?: ResourceKind): Iterable<{ entry: IResourceEntry; resource: object }>;

    counts(): ReadonlyMap<ResourceKind, ResourceKindCounts>;

    /**
     * Pins a resource: the service holds a strong reference until unpinned. This is the
     * ONLY way the registry retains a resource. Returns false if the resource is not
     * registered or already collected.
     */
    pin(target: object | IResourceEntry): boolean;
    unpin(target: object | IResourceEntry): boolean;
    isPinned(target: object | IResourceEntry): boolean;

    // #region usage tracking ("used by") — populated by the tracker's sweep
    /**
     * Records "consumer uses resource" (primitive snapshot only, deduped by
     * consumer uuid). Tagged with the current usage pass; a later
     * {@link completeUsagePass} prunes edges not re-seen. No-op for
     * unregistered resources.
     */
    recordUsage(resource: object, consumer: ResourceUsage, slot?: string): void;
    /**
     * Ends a full usage-collection pass: usages NOT re-recorded since the
     * previous completed pass are pruned (the edge is gone). Fires `updated`
     * for entries whose usage set changed.
     */
    completeUsagePass(): void;
    /** Consumer snapshots for a resource (stale up to one sweep interval). */
    getUsages(target: object | IResourceEntry): readonly ResourceUsage[];
    /**
     * False until the first full usage pass completed — before that, zero
     * usages means "not scanned yet", NOT "unused/dangling".
     */
    readonly usagesReady: boolean;

    /**
     * Records the loaded MODEL a resource ORIGINATED from — the glTF/FBX whose
     * subtree contained it at load time (recorded during the model walk). This
     * is the true "where did this come from", distinct from usage (who
     * references it now). `pointer` is the resource's glTF JSON pointer within
     * that model ("/textures/0"), when resolvable. Both must be registered;
     * no-op otherwise. Weak — dropped if the model is collected.
     */
    recordOrigin(resource: object, model: object, pointer?: string): void;
    /** The model a resource was loaded from (+ its glTF pointer), if recorded
     *  and the model is still alive. */
    getOrigin(target: object | IResourceEntry): ResourceOrigin | undefined;
    // #endregion
}

/** Where a resource was loaded from — the model entry + its glTF JSON pointer. */
export interface ResourceOrigin {
    readonly model: IResourceEntry;
    /** glTF JSON pointer within the model, e.g. "/textures/0". Absent for
     *  non-glTF sources or resources the loader didn't key. */
    readonly pointer?: string;
}

export interface ResourceKindCounts {
    readonly alive: number;
    readonly pinned: number;
    /** Total resources of this kind observed to be garbage collected so far. */
    readonly collected: number;
}
